import { useEffect } from 'react';
import { Routes, Route, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { GsLayout, GsHeader, GsSidebar, ToastProvider, useDarkMode, GsErrorBoundary } from '@gs/ui';
import type { SidebarItem } from '@gs/ui';
import { LayoutDashboard, RefreshCw, ScrollText, ShieldOff, Users } from 'lucide-react';
import { useAuth, GsRole, hasRole } from '@gs/auth';

import MobileBottomNav from './components/MobileBottomNav';
import { registerApiAuth, clearApiAuth } from './services/api';

import DashboardPage from './pages/DashboardPage';
import SyncManagerPage from './pages/SyncManagerPage';
import LogsPage from './pages/LogsPage';
import UsersPage from './pages/UsersPage';

const navItems: SidebarItem[] = [
  { key: 'dashboard', label: 'Tableau de bord', icon: LayoutDashboard, path: '/' },
  { key: 'users', label: 'Utilisateurs', icon: Users, path: '/utilisateurs' },
  { key: 'syncs', label: 'Synchronisations', icon: RefreshCw, path: '/syncs' },
  { key: 'logs', label: 'Journaux ETL', icon: ScrollText, path: '/logs' },
];

function adminPageTitle(pathname: string): string {
  if (pathname === '/' || pathname === '') return 'Tableau de bord';
  if (pathname.startsWith('/utilisateurs')) return 'Utilisateurs';
  if (pathname.startsWith('/syncs')) return 'Synchronisations';
  if (pathname.startsWith('/logs')) return 'Journaux ETL';
  return 'GS-Admin';
}

function gsRoleLabel(role: GsRole): string {
  switch (role) {
    case GsRole.ADMIN: return 'Administrateur';
    case GsRole.GESTIONNAIRE: return 'Gestionnaire';
    default: return 'Utilisateur';
  }
}

export default function App() {
  const location = useLocation();
  const navigate = useNavigate();
  const { isDark, toggle: toggleDark } = useDarkMode();
  const { user, isLoading, login, logout, getAccessToken } = useAuth();

  // ── Enregistrement SYNCHRONE du token ────────────────────────────────────
  // Appelé pendant le rendu, pas dans useEffect — garantit que le token
  // est disponible avant que TanStack Query monte et lance ses requêtes.
  if (user) {
    registerApiAuth(getAccessToken);
  } else {
    clearApiAuth();
  }

  useEffect(() => {
    return () => { clearApiAuth(); };
  }, []);

  if (isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-brand-500 border-t-transparent" />
      </div>
    );
  }

  if (!user) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-[#fbfbfb] dark:bg-slate-900">
        <div className="text-center">
          <div className="mx-auto mb-6 flex h-16 w-16 items-center justify-center">
            <img src="/logo-gs.png" alt="GS" className="h-16 w-16 object-contain dark:hidden" />
            <img src="/logo-gs-white.png" alt="" className="hidden h-16 w-16 object-contain dark:block" />
          </div>
          <h1 className="mb-2 text-xl font-semibold text-slate-800 dark:text-slate-100">Groupe Signalisation</h1>
          <p className="mb-6 text-sm text-slate-500 dark:text-slate-400">
            Connectez-vous avec votre compte @groupesignalisation.ca
          </p>
          <button
            type="button"
            onClick={() => void login()}
            className="inline-flex items-center gap-2 rounded-lg bg-slate-800 hover:bg-slate-700 dark:bg-slate-600 dark:hover:bg-slate-500 px-6 py-3 text-[14px] font-semibold text-white transition-colors"
          >
            Se connecter avec Microsoft
          </button>
        </div>
      </div>
    );
  }

  if (!hasRole(user, GsRole.ADMIN)) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-[#fbfbfb] dark:bg-slate-900">
        <div className="text-center max-w-sm px-6">
          <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-2xl bg-red-50 dark:bg-red-950/30">
            <ShieldOff size={28} className="text-red-500" />
          </div>
          <h1 className="mb-2 text-[18px] font-semibold text-slate-800 dark:text-slate-100">
            Accès réservé aux administrateurs
          </h1>
          <p className="mb-6 text-[13px] text-slate-500 dark:text-slate-400">
            Votre compte ({user.email}) n'a pas les droits nécessaires pour accéder à GS-Admin.
          </p>
          <button
            type="button"
            onClick={() => void logout()}
            className="inline-flex items-center gap-2 rounded-lg bg-slate-800 hover:bg-slate-700 dark:bg-slate-600 px-5 py-2 text-[13px] font-semibold text-white transition-colors"
          >
            Se déconnecter
          </button>
        </div>
      </div>
    );
  }

  return (
    <ToastProvider>
      <GsLayout
        header={<GsHeader pageTitle={adminPageTitle(location.pathname)} />}
        sidebar={
          <GsSidebar
            variant="light"
            headerLogoSrc="/logo-gs.png"
            headerLogoSrcDark="/logo-gs-white.png"
            appName="GS-Admin"
            mainSectionLabel="Navigation"
            items={navItems}
            isAdmin={hasRole(user, GsRole.ADMIN)}
            activePath={location.pathname}
            onNavigate={(path) => navigate(path)}
            bottomUser={{
              name: user.name,
              email: user.email,
              role: gsRoleLabel(user.role),
              initials: user.initials,
            }}
            isDark={isDark}
            onToggleDark={toggleDark}
            onLogout={() => void logout()}
            appVersion="0.1.0"
          />
        }
        mobileNav={<MobileBottomNav />}
      >
        <GsErrorBoundary>
          <Routes>
            <Route path="/" element={<DashboardPage />} />
            <Route path="/utilisateurs" element={<UsersPage />} />
            <Route path="/syncs" element={<SyncManagerPage />} />
            <Route path="/logs" element={<LogsPage />} />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </GsErrorBoundary>
      </GsLayout>
    </ToastProvider>
  );
}
